/**
 * Attaching todos / activities / signature requests / certified mail to
 * the deals they belong to, without rebuilding the deals that did not
 * change.
 *
 * The three child tables were normalised out of the deals row so
 * concurrent writes could not lose each other. Everything that reads a
 * deal still expects `deal.todos` and friends, so they are merged back
 * on in one place — and that merge was `deals.map((d) => ({ ...d, ... }))`,
 * which hands back 300 brand-new deal objects every time any of the four
 * arrays changes.
 *
 * That is the expensive part. A new deal object is, to everything
 * downstream, a changed deal:
 *
 *   - the Deal Tracker's ~139 memoised cards all re-render
 *   - the estimator rollups re-run over every deal (~1,400 rows)
 *   - deal health re-runs over every deal
 *   - so does the commission approval queue
 *
 * And it is not a rare event. Four arrays arrive separately during
 * start-up, so the board is rebuilt four times before it settles. After
 * that, every activity written anywhere rebuilds it again — and an
 * activity is written on every stage change, so each person dragging a
 * card redraws the whole board for everyone watching. With one user that
 * reads as a stutter; with ten it is most of what the browser is doing.
 *
 * So the merge is incremental: a deal keeps its previous merged object
 * unless its own source row or one of its own child lists actually
 * changed, the child lists keep their previous array when their contents
 * are unchanged, and the output array itself is the previous array when
 * nothing at all moved — because a new array is what re-runs every memo
 * built on top of it.
 *
 * Row identity is what "changed" means here, and it is the right test:
 * the store replaces a row object when that row is written and leaves
 * every other row alone, so identity already tracks the writes.
 */

const EMPTY = Object.freeze([]);

const CHILD_KEYS = ['todos', 'activities', 'signatureRequests', 'certifiedMailJobs'];

// The board asks how long a deal has been quiet, which is one timestamp,
// not 2,563 rows. Activity ROWS are fetched per deal when a deal opens;
// every deal carries the stamp so deal health reads the same answer
// either way. See getDealActivityStamps in supabaseStore.js.
const EMPTY_STAMPS = new Map();

function sameRows(before, after) {
  if (before === after) return true;
  if (!before || before.length !== after.length) return false;
  for (let i = 0; i < before.length; i += 1) {
    if (before[i] !== after[i]) return false;
  }
  return true;
}

function groupByDeal(rows, dealIds) {
  const map = new Map();
  for (const row of rows || []) {
    const dealId = row?.dealId;
    if (!dealId || !dealIds.has(dealId)) continue;
    const list = map.get(dealId);
    if (list) list.push(row);
    else map.set(dealId, [row]);
  }
  return map;
}

/**
 * One attacher per app instance: it carries the previous result, which
 * is the whole point. Call it with the current deals and children.
 */
export function createDealChildrenAttacher() {
  let cacheById = new Map();
  let previous = null;

  return function attachDealChildren(deals, children = {}, activityStamps = EMPTY_STAMPS) {
    if (!Array.isArray(deals) || deals.length === 0) {
      cacheById = new Map();
      previous = Array.isArray(deals) ? deals : EMPTY;
      return previous;
    }

    const dealIds = new Set(deals.map((deal) => deal?.id));
    const grouped = {
      todos: groupByDeal(children.todos, dealIds),
      activities: groupByDeal(children.activities, dealIds),
      signatureRequests: groupByDeal(children.signatureRequests, dealIds),
      certifiedMailJobs: groupByDeal(children.certifiedMailJobs, dealIds),
    };

    const nextCache = new Map();
    let changed = !previous || previous.length !== deals.length;

    const merged = deals.map((deal, index) => {
      const cached = cacheById.get(deal?.id);
      // Each list keeps its previous array when the rows in it are the
      // same ones, so a reader memoised on `deal.activities` holds
      // still too.
      const lists = {};
      let listsHeld = true;
      for (const key of CHILD_KEYS) {
        const fresh = grouped[key].get(deal?.id) || EMPTY;
        const held = cached && sameRows(cached.lists[key], fresh) ? cached.lists[key] : fresh;
        if (held !== cached?.lists[key]) listsHeld = false;
        lists[key] = held;
      }
      const lastActivityAt = activityStamps.get(deal?.id) || null;
      if (cached && listsHeld && cached.source === deal && cached.lastActivityAt === lastActivityAt) {
        nextCache.set(deal.id, cached);
        if (!previous || previous[index] !== cached.merged) changed = true;
        return cached.merged;
      }
      const next = { ...deal, ...lists, lastActivityAt };
      nextCache.set(deal?.id, { source: deal, lists, lastActivityAt, merged: next });
      changed = true;
      return next;
    });

    cacheById = nextCache;
    // Nothing moved, so hand back the very array handed out last time:
    // a fresh array with identical contents still re-runs every memo
    // downstream of it.
    if (!changed && previous) return previous;
    previous = merged;
    return merged;
  };
}
